import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useOrg } from '../../../shared/contexts/OrgContext';
import { useMobile } from '../../../shared/hooks/useMobile';
import { extractOrgIdFromPath } from '../../../shared/utils/organizationUtils';
import Logo from '../../../shared/components/ui/Logo';
import TableView from '../components/TableView';
import DatabaseTablesSidebar from '../components/DatabaseTablesSidebar';
import AddDataModal from '../components/modals/AddDataModal';
import AddUserModal from '../components/modals/AddUserModal';
import EditUserModal from '../components/modals/EditUserModal';
import AddOrganizationModal from '../components/modals/AddOrganizationModal';
import EditOrganizationModal from '../components/modals/EditOrganizationModal';
import EditUserOrganisationModal from '../components/modals/EditUserOrganisationModal';
import AddUserOrganisationModal from '../components/modals/AddUserOrganisationModal';
import EditApiKeyModal from '../components/modals/EditApiKeyModal';
import AddApiKeyModal from '../components/modals/AddApiKeyModal';
import EditTournamentAccessModal from '../components/modals/EditTournamentAccessModal';
import AddTournamentAccessModal from '../components/modals/AddTournamentAccessModal';
import AddTeamAccessModal from '../components/modals/AddTeamAccessModal';
import EditTeamAccessModal from '../components/modals/EditTeamAccessModal';
import AddPlayerAccessModal from '../components/modals/AddPlayerAccessModal';
import EditPlayerAccessModal from '../components/modals/EditPlayerAccessModal';
import AddSeasonAccessModal from '../components/modals/AddSeasonAccessModal';
import EditSeasonAccessModal from '../components/modals/EditSeasonAccessModal';
import EditAppAccessModal from '../components/modals/EditAppAccessModal';
import AddAppAccessModal from '../components/modals/AddAppAccessModal';
import EditTournamentModal from '../components/modals/EditTournamentModal';
import AddTournamentModal from '../components/modals/AddTournamentModal';
import EditSeasonModal from '../components/modals/EditSeasonModal';
import AddSeasonModal from '../components/modals/AddSeasonModal';
import EditTeamModal from '../components/modals/EditTeamModal';
import AddTeamModal from '../components/modals/AddTeamModal';
import EditPlayerModal from '../components/modals/EditPlayerModal';
import AddPlayerModal from '../components/modals/AddPlayerModal';
import EditStatTypeModal from '../components/modals/EditStatTypeModal';
import AddStatTypeModal from '../components/modals/AddStatTypeModal';
import Toast from '../../../shared/components/ui/Toast';
import ClientIntegrationsPage from './ClientIntegrationsPage';
import ParentSocialMediaSettingsPage from './ParentSocialMediaSettingsPage';
import BillingPage from './BillingPage';
import { organizationsService, userOrganisationsService, usersService, apiKeysService, tournamentAccessService, appAccessService, tournamentsService, seasonsService, teamsService, playersService, teamAccessService, playerAccessService, seasonAccessService, statTypesService } from '../services';

// Define the database tables that we want to manage
export interface DatabaseTable {
  id: string;
  name: string;
  displayName: string;
  description: string;
  icon: string;
}

interface SidebarItem {
  id: string;
  name: string;
  icon: string;
  isActive?: boolean;
}

const DATABASE_TABLES: DatabaseTable[] = [
  // Core entities
  { id: 'organisations', name: 'organisations', displayName: 'Organizations', description: 'Manage organization data and settings', icon: '🏢' },
  { id: 'users', name: 'users', displayName: 'Users', description: 'Manage user accounts and profiles', icon: '👥' },
  { id: 'user_organisations', name: 'user_organisations', displayName: 'User Organizations', description: 'Link users to their organizations', icon: '🔗' },
  { id: 'api_keys', name: 'api_keys', displayName: 'API Keys', description: 'Manage API keys and authentication', icon: '🔑' },
  { id: 'app_access', name: 'app_access', displayName: 'App Access', description: 'Manage application access controls', icon: '🚪' },
  { id: 'tournaments', name: 'tournaments', displayName: 'Tournaments', description: 'Manage tournaments and competitions', icon: '🏟️' },
  { id: 'seasons', name: 'seasons', displayName: 'Seasons', description: 'Manage seasons and time periods', icon: '📅' },
  { id: 'teams', name: 'teams', displayName: 'Teams', description: 'Manage teams and organizations', icon: '⚽' },
  { id: 'players', name: 'players', displayName: 'Players', description: 'Manage players and athletes', icon: '👤' },
  // Access tables (grouped together)
  { id: 'tournament_access', name: 'tournament_access', displayName: 'Tournament Access', description: 'Control tournament access permissions', icon: '🏆' },
  { id: 'team_access', name: 'team_access', displayName: 'Team Access', description: 'Manage team access permissions', icon: '🧑‍🤝‍🧑' },
  { id: 'season_access', name: 'season_access', displayName: 'Season Access', description: 'Manage season access permissions', icon: '🗂️' },
  { id: 'player_access', name: 'player_access', displayName: 'Player Access', description: 'Manage player access permissions', icon: '👤' },
  { id: 'stat_types', name: 'stat_types', displayName: 'Stat Types', description: 'Manage statistic types for scoresheets and events', icon: '📊' },
];

const ControlRoomApp: React.FC = () => {
  const { isMobile } = useMobile();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(isMobile); // Closed by default on mobile
  const [selectedTable, setSelectedTable] = useState<DatabaseTable | null>(null);
      const [activeTopNavItem, setActiveTopNavItem] = useState('for-you');
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [editingEntity, setEditingEntity] = useState<any>(null);
  const [isEditUserOrgModalOpen, setIsEditUserOrgModalOpen] = useState(false);
  const [editingUserOrg, setEditingUserOrg] = useState<any>(null);
  const [isAddUserOrgModalOpen, setIsAddUserOrgModalOpen] = useState(false);
  const [isEditApiKeyModalOpen, setIsEditApiKeyModalOpen] = useState(false);
  const [editingApiKey, setEditingApiKey] = useState<any>(null);
  const [isAddApiKeyModalOpen, setIsAddApiKeyModalOpen] = useState(false);
  const [isEditTournamentAccessModalOpen, setIsEditTournamentAccessModalOpen] = useState(false);
  const [editingTournamentAccess, setEditingTournamentAccess] = useState<any>(null);
  const [isAddTournamentAccessModalOpen, setIsAddTournamentAccessModalOpen] = useState(false);
  const [isEditAppAccessModalOpen, setIsEditAppAccessModalOpen] = useState(false);
  const [editingAppAccess, setEditingAppAccess] = useState<any>(null);
  const [isAddAppAccessModalOpen, setIsAddAppAccessModalOpen] = useState(false);
  const [isEditTournamentModalOpen, setIsEditTournamentModalOpen] = useState(false);
  const [editingTournament, setEditingTournament] = useState<any>(null);
  const [isAddTournamentModalOpen, setIsAddTournamentModalOpen] = useState(false);
  const [isEditSeasonModalOpen, setIsEditSeasonModalOpen] = useState(false);
  const [editingSeason, setEditingSeason] = useState<any>(null);
  const [isAddSeasonModalOpen, setIsAddSeasonModalOpen] = useState(false);
  const [isEditTeamModalOpen, setIsEditTeamModalOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<any>(null);
  const [isAddTeamModalOpen, setIsAddTeamModalOpen] = useState(false);
  const [isEditPlayerModalOpen, setIsEditPlayerModalOpen] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState<any>(null);
  const [isAddPlayerModalOpen, setIsAddPlayerModalOpen] = useState(false);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [isAddOrganizationModalOpen, setIsAddOrganizationModalOpen] = useState(false);
  const [isEditOrganizationModalOpen, setIsEditOrganizationModalOpen] = useState(false);
  const [editingOrganization, setEditingOrganization] = useState<any>(null);
  const [isAddTeamAccessModalOpen, setIsAddTeamAccessModalOpen] = useState(false);
  const [isEditTeamAccessModalOpen, setIsEditTeamAccessModalOpen] = useState(false);
  const [editingTeamAccess, setEditingTeamAccess] = useState<any>(null);
  const [isAddPlayerAccessModalOpen, setIsAddPlayerAccessModalOpen] = useState(false);
  const [isEditPlayerAccessModalOpen, setIsEditPlayerAccessModalOpen] = useState(false);
  const [editingPlayerAccess, setEditingPlayerAccess] = useState<any>(null);
  const [isAddSeasonAccessModalOpen, setIsAddSeasonAccessModalOpen] = useState(false);
  const [isEditSeasonAccessModalOpen, setIsEditSeasonAccessModalOpen] = useState(false);
  const [editingSeasonAccess, setEditingSeasonAccess] = useState<any>(null);
  const [isAddStatTypeModalOpen, setIsAddStatTypeModalOpen] = useState(false);
  const [isEditStatTypeModalOpen, setIsEditStatTypeModalOpen] = useState(false);
  const [editingStatType, setEditingStatType] = useState<any>(null);
  const [toast, setToast] = useState<{isVisible: boolean, type: 'success' | 'error' | 'info', message: string}>({
    isVisible: false,
    type: 'success',
    message: ''
  });
  const { signOut } = useAuth();
  const { currentOrganization } = useOrg();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  // Extract organization ID from URL for organization-specific routes
  const orgIdFromPath = params.orgId || extractOrgIdFromPath(location.pathname);

  // Handle table selection from URL (special pages are not DB tables)
  useEffect(() => {
    const pathParts = location.pathname.split('/');
    const tableName = pathParts[pathParts.length - 1];
    const isBillingPage =
      location.pathname.includes('/control-room/plans') ||
      location.pathname.includes('/control-room/pricing-rules') ||
      location.pathname.includes('/control-room/invoices') ||
      location.pathname.includes('/control-room/runs') ||
      location.pathname.includes('/control-room/billing-settings');

    if (
      location.pathname.includes('client-integrations') ||
      location.pathname.includes('parent-social-media') ||
      isBillingPage
    ) {
      setSelectedTable(null);
      return;
    }
    if (tableName !== 'control-room' && tableName !== orgIdFromPath) {
      const table = DATABASE_TABLES.find(t => t.name === tableName);
      if (table && selectedTable?.id !== table.id) {
        setSelectedTable(table);
      }
    } else {
      setSelectedTable(null);
    }
  }, [location.pathname, orgIdFromPath, selectedTable]);

  // Handle active navigation based on URL
  useEffect(() => {
    const pathParts = location.pathname.split('/');
    const lastPart = pathParts[pathParts.length - 1];
    
    // If we're on the control-room route (home), set for-you as active
    if (lastPart === 'control-room' || lastPart === orgIdFromPath) {
      setActiveTopNavItem('for-you');
    }
    // For other routes, summary and recent are never active (they have no pages)
  }, [location.pathname, orgIdFromPath]);
  

  const sidebarItems: SidebarItem[] = [
    { id: 'for-you', name: 'For you', icon: '⭐', isActive: activeTopNavItem === 'for-you' },
    { id: 'summary', name: 'Summary', icon: '📊', isActive: false },
    { id: 'recent', name: 'Recent', icon: '🕒', isActive: false },
    { id: 'dashboard', name: 'Go back to Dashboard', icon: '🏠', isActive: false }
  ];

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('❌ Logout error:', error);
      // Force navigation even if logout fails
      navigate('/');
    }
  };

  // Handle logo click - navigate to homepage
  const handleLogoClick = () => {
    // Use the current organization ID if available, otherwise use orgIdFromPath
    const actualOrgId = currentOrganization?.id || orgIdFromPath || 'default';
    navigate(`/o/${actualOrgId}`);
  };

  // Handle sidebar item click
  const handleSidebarItemClick = (itemId: string) => {
    if (itemId === 'dashboard') {
      // Navigate to homepage
      const actualOrgId = currentOrganization?.id || orgIdFromPath || 'default';
      navigate(`/o/${actualOrgId}`);
    } else if (itemId === 'for-you') {
      // Navigate to Control Room home
      setActiveTopNavItem('for-you');
      setSelectedTable(null); // Clear any selected table
      navigate(`/o/${orgIdFromPath}/control-room`);
    }
    // Summary, Recent have no navigation (empty handlers)
  };

  // Organization creation disabled - use Supabase dashboard for new organizations

  // Handle edit entity (organization, users, api_keys, user_organisation, or tournament_access)
  const handleEditEntity = (entity: any) => {
    if (selectedTable?.name === 'organisations') {
      setEditingOrganization(entity);
      setIsEditOrganizationModalOpen(true);
    } else if (selectedTable?.name === 'user_organisations') {
      setEditingUserOrg(entity);
      setIsEditUserOrgModalOpen(true);
    } else if (selectedTable?.name === 'users') {
      setEditingUser(entity);
      setIsEditUserModalOpen(true);
    } else if (selectedTable?.name === 'api_keys') {
      setEditingApiKey(entity);
      setIsEditApiKeyModalOpen(true);
    } else if (selectedTable?.name === 'tournament_access') {
      setEditingTournamentAccess(entity);
      setIsEditTournamentAccessModalOpen(true);
    } else if (selectedTable?.name === 'app_access') {
      setEditingAppAccess(entity);
      setIsEditAppAccessModalOpen(true);
    } else if (selectedTable?.name === 'tournaments') {
      setEditingTournament(entity);
      setIsEditTournamentModalOpen(true);
    } else if (selectedTable?.name === 'seasons') {
      setEditingSeason(entity);
      setIsEditSeasonModalOpen(true);
    } else if (selectedTable?.name === 'teams') {
      setEditingTeam(entity);
      setIsEditTeamModalOpen(true);
    } else if (selectedTable?.name === 'players') {
      setEditingPlayer(entity);
      setIsEditPlayerModalOpen(true);
    } else if (selectedTable?.name === 'player_access') {
      setEditingPlayerAccess(entity);
      setIsEditPlayerAccessModalOpen(true);
    } else if (selectedTable?.name === 'team_access') {
      setEditingTeamAccess(entity);
      setIsEditTeamAccessModalOpen(true);
    } else if (selectedTable?.name === 'season_access') {
      setEditingSeasonAccess(entity);
      setIsEditSeasonAccessModalOpen(true);
    } else if (selectedTable?.name === 'stat_types') {
      setEditingStatType(entity);
      setIsEditStatTypeModalOpen(true);
    } else {
      setEditingEntity(entity);
      setIsAddModalOpen(true);
    }
  };

  // Handle save entity (update only - creation disabled)
  const handleSaveEntity = async (data: any) => {
    if (!editingEntity) {
      throw new Error('Entity creation is disabled. Please use Supabase dashboard to create entities.');
    }
    
    try {
      console.log(`Updating ${selectedTable?.name} with data:`, data);
      
      let result;
      if (selectedTable?.name === 'organisations') {
        result = await organizationsService.update(editingEntity.id, data);
      } else if (selectedTable?.name === 'users') {
        result = await usersService.update(editingEntity.id, data);
      } else if (selectedTable?.name === 'api_keys') {
        result = await apiKeysService.update(editingEntity.id, data);
      } else if (selectedTable?.name === 'tournament_access') {
        result = await tournamentAccessService.update(editingEntity.id, data);
      } else if (selectedTable?.name === 'tournaments') {
        result = await tournamentsService.update(editingEntity.id, data);
      } else if (selectedTable?.name === 'seasons') {
        result = await seasonsService.update(editingEntity.id, data);
      } else if (selectedTable?.name === 'teams') {
        result = await teamsService.update(editingEntity.id, data);
      } else if (selectedTable?.name === 'players') {
        result = await playersService.update(editingEntity.id, data);
      } else if (selectedTable?.name === 'team_access') {
        result = await teamAccessService.update(editingEntity.id, data);
      } else if (selectedTable?.name === 'player_access') {
        result = await playerAccessService.update(editingEntity.id, data);
      } else if (selectedTable?.name === 'season_access') {
        result = await seasonAccessService.update(editingEntity.id, data);
      } else {
        throw new Error('Unknown table type for editing');
      }
      
      console.log(`${selectedTable?.name} updated successfully:`, result);
      // Trigger table refresh
      setRefreshCounter((prev: number) => prev + 1);
      setEditingEntity(null); // Clear editing state
    } catch (error) {
      console.error(`Error updating ${selectedTable?.name}:`, error);
      throw error; // Re-throw to let modal handle it
    }
  };

  // Handle save user organisation
  const handleSaveUserOrg = async (data: any) => {
    if (!editingUserOrg) {
      throw new Error('No user organisation selected for editing');
    }
    
    try {
      console.log('Updating user_organisation with data:', data);
      
      const result = await userOrganisationsService.update(editingUserOrg.id, data);
      
      console.log('User organisation updated successfully:', result);
      // Trigger table refresh
      setRefreshCounter((prev: number) => prev + 1);
      setEditingUserOrg(null); // Clear editing state
    } catch (error) {
      console.error('Error updating user organisation:', error);
      throw error;
    }
  };

  // Handle add user organisation
  const handleAddUserOrg = async (data: any) => {
    try {
      console.log('Creating user_organisation with data:', data);
   
      const result = await userOrganisationsService.create(data);
      
      console.log('User organisation created successfully:', result);
      // Trigger table refresh
      setRefreshCounter((prev: number) => prev + 1);
    } catch (error) {
      console.error('Error creating user organisation:', error);
      throw error;
    }
  };

  // Handle save API key (update)
  const handleSaveApiKey = async (data: any) => {
    if (!editingApiKey) {
      throw new Error('No API key selected for editing');
    }
    
    try {
      console.log('Updating API key with data:', data);
      
      const result = await apiKeysService.update(editingApiKey.id, data);
      
      console.log('API key updated successfully:', result);
      // Trigger table refresh
      setRefreshCounter((prev: number) => prev + 1);
      setEditingApiKey(null); // Clear editing state
      
      // Show success toast
      setToast({
        isVisible: true,
        type: 'success',
        message: 'API key updated successfully!'
      });
    } catch (error) {
      console.error('Error updating API key:', error);
      
      // Show error toast
      setToast({
        isVisible: true,
        type: 'error',
        message: 'Error updating API key. Please try again.'
      });
      throw error;
    }
  };

  // Handle add API key
  const handleAddApiKey = async (data: any) => {
    try {
      console.log('Creating API key with data:', data);
   
      const result = await apiKeysService.create(data);
      
      console.log('API key created successfully:', result);
      // Trigger table refresh
      setRefreshCounter((prev: number) => prev + 1);
      
      // Show success toast
      setToast({
        isVisible: true,
        type: 'success',
        message: 'API key created successfully!'
      });
    } catch (error) {
      console.error('Error creating API key:', error);
      
      // Show error toast
      setToast({
        isVisible: true,
        type: 'error',
        message: 'Error creating API key. Please try again.'
      });
      throw error;
    }
  };

  // Handle save tournament access
  const handleSaveTournamentAccess = async (data: any) => {
    if (!editingTournamentAccess) {
      throw new Error('No tournament access selected for editing');
    }
    
    try {
      console.log('Updating tournament access with data:', data);
      
      const result = await tournamentAccessService.update(editingTournamentAccess.id, data);
      
      console.log('Tournament access updated successfully:', result);
      // Trigger table refresh
      setRefreshCounter((prev: number) => prev + 1);
      setEditingTournamentAccess(null); // Clear editing state
    } catch (error) {
      console.error('Error updating tournament access:', error);
      throw error;
    }
  };

  // Handle add tournament access
  const handleAddTournamentAccess = async (data: any) => {
    try {
      console.log('Creating tournament access with data:', data);
   
      const result = await tournamentAccessService.create(data);
   
      console.log('Tournament access created successfully:', result);
      // Trigger table refresh
      setRefreshCounter((prev: number) => prev + 1);
    } catch (error) {
      console.error('Error creating tournament access:', error);
      throw error;
    }
  };

  // Handle save app access
  const handleSaveAppAccess = async (id: string, data: any) => {
    try {
      console.log('Updating app access with data:', data);
      
      const result = await appAccessService.update(id, data);
      
      console.log('App access updated successfully:', result);
      // Trigger table refresh
      setRefreshCounter((prev: number) => prev + 1);
      setEditingAppAccess(null); // Clear editing state
      
      // Show success toast
      setToast({
        isVisible: true,
        type: 'success',
        message: 'App access updated successfully!'
      });
    } catch (error) {
      console.error('Error updating app access:', error);
      
      // Show error toast
      setToast({
        isVisible: true,
        type: 'error',
        message: 'Error updating app access. Please try again.'
      });
      throw error;
    }
  };

  // Handle add app access
  const handleAddAppAccess = async (data: any) => {
    try {
      console.log('Creating app access with data:', data);
   
      const result = await appAccessService.create(data);
   
      console.log('App access created successfully:', result);
      // Trigger table refresh
      setRefreshCounter((prev: number) => prev + 1);
      
      // Show success toast
      setToast({
        isVisible: true,
        type: 'success',
        message: 'App access created successfully!'
      });
    } catch (error) {
      console.error('Error creating app access:', error);
      
      // Show error toast
      setToast({
        isVisible: true,
        type: 'error',
        message: 'Error creating app access. Please try again.'
      });
      throw error;
    }
  };

  // Handle save tournament (update)
  const handleSaveTournament = async (data: any) => {
    if (!editingTournament) {
      throw new Error('No tournament selected for editing');
    }
    
    try {
      console.log('Updating tournament with data:', data);
      const result = await tournamentsService.update(editingTournament.id, data);
      
      console.log('Tournament updated successfully:', result);
      // Trigger table refresh
      setRefreshCounter((prev: number) => prev + 1);
      
      // Show success toast
      setToast({
        isVisible: true,
        type: 'success',
        message: 'Tournament updated successfully!'
      });
    } catch (error) {
      console.error('Error updating tournament:', error);
      
      // Show error toast
      setToast({
        isVisible: true,
        type: 'error',
        message: 'Error updating tournament. Please try again.'
      });
      throw error;
    }
  };

  // Handle add tournament
  const handleAddTournament = async (data: any) => {
    try {
      console.log('Creating tournament with data:', data);
   
      const result = await tournamentsService.create(data);
   
      console.log('Tournament created successfully:', result);
      // Trigger table refresh
      setRefreshCounter((prev: number) => prev + 1);
      
      // Show success toast
      setToast({
        isVisible: true,
        type: 'success',
        message: 'Tournament created successfully!'
      });
    } catch (error) {
      console.error('Error creating tournament:', error);
      
      // Show error toast
      setToast({
        isVisible: true,
        type: 'error',
        message: 'Error creating tournament. Please try again.'
      });
      throw error;
    }
  };

  // Handle save season (update)
  const handleSaveSeason = async (data: any) => {
    if (!editingSeason) {
      throw new Error('No season selected for editing');
    }
    
    try {
      console.log('Updating season with data:', data);
      const result = await seasonsService.update(editingSeason.id, data);
      
      console.log('Season updated successfully:', result);
      // Trigger table refresh
      setRefreshCounter((prev: number) => prev + 1);
      
      // Show success toast
      setToast({
        isVisible: true,
        type: 'success',
        message: 'Season updated successfully!'
      });
    } catch (error) {
      console.error('Error updating season:', error);
      
      // Show error toast
      setToast({
        isVisible: true,
        type: 'error',
        message: 'Error updating season. Please try again.'
      });
      throw error;
    }
  };

  // Handle add season
  const handleAddSeason = async (data: any) => {
    try {
      console.log('Creating season with data:', data);
   
      const result = await seasonsService.create(data);
   
      console.log('Season created successfully:', result);
      // Trigger table refresh
      setRefreshCounter((prev: number) => prev + 1);
      
      // Show success toast
      setToast({
        isVisible: true,
        type: 'success',
        message: 'Season created successfully!'
      });
    } catch (error) {
      console.error('Error creating season:', error);
      
      // Show error toast
      setToast({
        isVisible: true,
        type: 'error',
        message: 'Error creating season. Please try again.'
      });
      throw error;
    }
  };

  // Handle save team (update)
  const handleSaveTeam = async (data: any) => {
    if (!editingTeam) {
      throw new Error('No team selected for editing');
    }
    
    try {
      console.log('Updating team with data:', data);
      const result = await teamsService.update(editingTeam.id, data);
      
      console.log('Team updated successfully:', result);
      // Trigger table refresh
      setRefreshCounter((prev: number) => prev + 1);
      
      // Show success toast
      setToast({
        isVisible: true,
        type: 'success',
        message: 'Team updated successfully!'
      });
    } catch (error) {
      console.error('Error updating team:', error);
      
      // Show error toast
      setToast({
        isVisible: true,
        type: 'error',
        message: 'Error updating team. Please try again.'
      });
      throw error;
    }
  };

  // Handle add team
  const handleAddTeam = async (data: any) => {
    try {
      console.log('Creating team with data:', data);
   
      const result = await teamsService.create(data);
   
      console.log('Team created successfully:', result);
      // Trigger table refresh
      setRefreshCounter((prev: number) => prev + 1);
      
      // Show success toast
      setToast({
        isVisible: true,
        type: 'success',
        message: 'Team created successfully!'
      });
    } catch (error) {
      console.error('Error creating team:', error);
      
      // Show error toast
      setToast({
        isVisible: true,
        type: 'error',
        message: 'Error creating team. Please try again.'
      });
      throw error;
    }
  };

  // Handle save player (update)
  const handleSavePlayer = async (data: any) => {
    if (!editingPlayer) {
      throw new Error('No player selected for editing');
    }
    
    try {
      console.log('Updating player with data:', data);
      const result = await playersService.update(editingPlayer.id, data);
      
      console.log('Player updated successfully:', result);
      // Trigger table refresh
      setRefreshCounter((prev: number) => prev + 1);
      
      // Show success toast
      setToast({
        isVisible: true,
        type: 'success',
        message: 'Player updated successfully!'
      });
    } catch (error) {
      console.error('Error updating player:', error);
      
      // Show error toast
      setToast({
        isVisible: true,
        type: 'error',
        message: 'Error updating player. Please try again.'
      });
      throw error;
    }
  };

  // Handle add player
  const handleAddPlayer = async (data: any) => {
    try {
      console.log('Creating player with data:', data);
   
      const result = await playersService.create(data);
   
      console.log('Player created successfully:', result);
      // Trigger table refresh
      setRefreshCounter((prev: number) => prev + 1);
      
      // Show success toast
      setToast({
        isVisible: true,
        type: 'success',
        message: 'Player created successfully!'
      });
    } catch (error) {
      console.error('Error creating player:', error);
      
      // Show error toast
      setToast({
        isVisible: true,
        type: 'error',
        message: 'Error creating player. Please try again.'
      });
      throw error;
    }
  };

  // Handle save stat type (edit)
  const handleSaveStatType = async (data: any) => {
    if (!editingStatType) {
      throw new Error('No stat type selected for editing');
    }
    try {
      await statTypesService.update(editingStatType.id, data);
      setRefreshCounter((prev) => prev + 1);
      setEditingStatType(null);
      setToast({ isVisible: true, type: 'success', message: 'Stat type updated successfully!' });
    } catch (error) {
      console.error('Error updating stat type:', error);
      setToast({ isVisible: true, type: 'error', message: 'Error updating stat type. Please try again.' });
      throw error;
    }
  };

  // Handle add stat type
  const handleAddStatType = async (data: any) => {
    try {
      await statTypesService.create(data);
      setRefreshCounter((prev) => prev + 1);
      setToast({ isVisible: true, type: 'success', message: 'Stat type created successfully!' });
    } catch (error) {
      console.error('Error creating stat type:', error);
      setToast({ isVisible: true, type: 'error', message: 'Error creating stat type. Please try again.' });
      throw error;
    }
  };

  // Handle add team access
  const handleAddTeamAccess = async (data: any) => {
    try {
      console.log('Creating team access with data:', data);
   
      const result = await teamAccessService.create(data);
   
      console.log('Team access created successfully:', result);
      // Trigger table refresh
      setRefreshCounter((prev: number) => prev + 1);
      
      // Show success toast
      setToast({
        isVisible: true,
        type: 'success',
        message: 'Team access created successfully!'
      });
    } catch (error) {
      console.error('Error creating team access:', error);
      
      // Show error toast
      setToast({
        isVisible: true,
        type: 'error',
        message: 'Error creating team access. Please try again.'
      });
      throw error;
    }
  };

  // Handle add player access
  const handleAddPlayerAccess = async (data: any) => {
    try {
      console.log('Creating player access with data:', data);
   
      const result = await playerAccessService.create(data);
   
      console.log('Player access created successfully:', result);
      // Trigger table refresh
      setRefreshCounter((prev: number) => prev + 1);
      
      // Show success toast
      setToast({
        isVisible: true,
        type: 'success',
        message: 'Player access created successfully!'
      });
    } catch (error) {
      console.error('Error creating player access:', error);
      
      // Show error toast
      setToast({
        isVisible: true,
        type: 'error',
        message: 'Error creating player access. Please try again.'
      });
      throw error;
    }
  };

  // Handle add season access
  const handleAddSeasonAccess = async (data: any) => {
    try {
      console.log('Creating season access with data:', data);
   
      const result = await seasonAccessService.create(data);
   
      console.log('Season access created successfully:', result);
      // Trigger table refresh
      setRefreshCounter((prev: number) => prev + 1);
      
      // Show success toast
      setToast({
        isVisible: true,
        type: 'success',
        message: 'Season access created successfully!'
      });
    } catch (error) {
      console.error('Error creating season access:', error);
      
      // Show error toast
      setToast({
        isVisible: true,
        type: 'error',
        message: 'Error creating season access. Please try again.'
      });
      throw error;
    }
  };

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {/* Mobile Overlay */}
      {isMobile && !isSidebarCollapsed && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsSidebarCollapsed(true)}
        />
      )}

      {/* Sidebar - Always show for Control Room */}
      {true && (
        <div className={`bg-white border-r border-gray-200 transition-all duration-300 flex-shrink-0 relative ${
          isMobile 
            ? (isSidebarCollapsed ? 'w-0 overflow-hidden' : 'w-80') 
            : (isSidebarCollapsed ? 'w-16' : 'w-64')
        } ${isMobile && !isSidebarCollapsed ? 'z-50 shadow-lg' : ''}`}>
          <div className="p-3 sm:p-4 flex flex-col h-full">
            {/* Logo - Now Clickable */}
            <div className="flex items-center justify-start mb-3 sm:mb-4">
              <button
                onClick={handleLogoClick}
                className="flex items-center space-x-2 hover:opacity-80 transition-opacity duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-md p-1 min-h-44 min-w-44"
                title="Go to Homepage"
                aria-label="Go to Homepage"
              >
                <Logo 
                  size="md" 
                  variant={isSidebarCollapsed ? 'mark' : 'full'} 
                  className="flex items-center space-x-2"
                />
              </button>
            </div>

            {/* Expand/Collapse Button - Same behavior as other apps */}
            <div className="absolute top-3 sm:top-4 right-0 transform translate-x-1/2 z-50 pointer-events-auto">
              <button
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                className="w-6 h-6 bg-white border border-gray-200 rounded-full shadow-sm flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors min-h-44 min-w-44"
                title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isSidebarCollapsed ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  )}
                </svg>
              </button>
            </div>

            {/* Scrollable nav area - allows left panel to scroll when content overflows */}
            <div className="flex-1 min-h-0 overflow-y-auto flex flex-col">
            {/* Main Section */}
            <div className="flex-shrink-0 mb-1">
              <nav className="space-y-1">
                {sidebarItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleSidebarItemClick(item.id)}
                    className={`w-full flex items-center rounded-lg text-left transition-colors min-h-44 ${
                      isSidebarCollapsed 
                        ? 'justify-center px-2 py-1.5' 
                        : 'space-x-2 sm:space-x-3 px-2 sm:px-3 py-1.5'
                    } ${
                      item.isActive
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-sm sm:text-base">{item.icon}</span>
                    {!isSidebarCollapsed && (
                      <span className="text-xs sm:text-sm font-normal">{item.name}</span>
                    )}
                  </button>
                ))}
                
              </nav>
            </div>

            {/* Divider */}
            {!isSidebarCollapsed && (
              <div className="border-t border-gray-200 mb-1 flex-shrink-0" />
            )}

            {/* Integrations Section - Client Integration sub-options */}
            {!isSidebarCollapsed && (
              <div className="px-3 py-2 flex-shrink-0">
                <div className="text-base font-semibold text-gray-900 mb-2">Integrations</div>
                <div className="space-y-0.5">
                  <div className="text-xs font-medium text-gray-500 px-2 py-1">Client Integrations</div>
                  <button
                    onClick={() => {
                      setSelectedTable(null);
                      navigate(`/o/${orgIdFromPath}/control-room/client-integrations/data-mappings`);
                    }}
                    className={`w-full flex items-center space-x-2 px-2 py-1.5 text-sm rounded-lg transition-colors ${
                      location.pathname.includes('client-integrations') && !location.pathname.includes('api-visibility')
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <span>📋</span>
                    <span className="truncate">Data Mappings</span>
                  </button>
                  <button
                    onClick={() => {
                      setSelectedTable(null);
                      navigate(`/o/${orgIdFromPath}/control-room/client-integrations/api-visibility`);
                    }}
                    className={`w-full flex items-center space-x-2 px-2 py-1.5 text-sm rounded-lg transition-colors ${
                      location.pathname.includes('api-visibility')
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <span>👁</span>
                    <span className="truncate">API visibility</span>
                  </button>
                  <div className="text-xs font-medium text-gray-500 px-2 py-1 mt-2">Social Media</div>
                  <button
                    onClick={() => {
                      setSelectedTable(null);
                      navigate(`/o/${orgIdFromPath}/control-room/parent-social-media`);
                    }}
                    className={`w-full flex items-center space-x-2 px-2 py-1.5 text-sm rounded-lg transition-colors ${
                      location.pathname.includes('parent-social-media')
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <span>📱</span>
                    <span className="truncate">Parent Accounts</span>
                  </button>
                </div>
              </div>
            )}

            {/* Divider before Database Tables */}
            {!isSidebarCollapsed && (
              <div className="border-t border-gray-200 mb-1 flex-shrink-0" />
            )}

            {/* Billing Section */}
            {!isSidebarCollapsed && (
              <div className="px-3 py-2 flex-shrink-0">
                <div className="text-base font-semibold text-gray-900 mb-2">Billing</div>
                <div className="space-y-0.5">
                  <button
                    onClick={() => {
                      setSelectedTable(null);
                      navigate(`/o/${orgIdFromPath}/control-room/plans`);
                    }}
                    className={`w-full flex items-center space-x-2 px-2 py-1.5 text-sm rounded-lg transition-colors ${
                      location.pathname.includes('/control-room/plans')
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <span>💳</span>
                    <span className="truncate">Plans</span>
                  </button>
                  <button
                    onClick={() => {
                      setSelectedTable(null);
                      navigate(`/o/${orgIdFromPath}/control-room/pricing-rules`);
                    }}
                    className={`w-full flex items-center space-x-2 px-2 py-1.5 text-sm rounded-lg transition-colors ${
                      location.pathname.includes('/control-room/pricing-rules')
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <span>🧮</span>
                    <span className="truncate">Pricing Rules</span>
                  </button>
                  <button
                    onClick={() => {
                      setSelectedTable(null);
                      navigate(`/o/${orgIdFromPath}/control-room/invoices`);
                    }}
                    className={`w-full flex items-center space-x-2 px-2 py-1.5 text-sm rounded-lg transition-colors ${
                      location.pathname.includes('/control-room/invoices')
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <span>🧾</span>
                    <span className="truncate">Invoices</span>
                  </button>
                  <button
                    onClick={() => {
                      setSelectedTable(null);
                      navigate(`/o/${orgIdFromPath}/control-room/runs`);
                    }}
                    className={`w-full flex items-center space-x-2 px-2 py-1.5 text-sm rounded-lg transition-colors ${
                      location.pathname.includes('/control-room/runs')
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <span>⚙️</span>
                    <span className="truncate">Runs</span>
                  </button>
                  <button
                    onClick={() => {
                      setSelectedTable(null);
                      navigate(`/o/${orgIdFromPath}/control-room/billing-settings`);
                    }}
                    className={`w-full flex items-center space-x-2 px-2 py-1.5 text-sm rounded-lg transition-colors ${
                      location.pathname.includes('/control-room/billing-settings')
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <span>🛠️</span>
                    <span className="truncate">Billing Settings</span>
                  </button>
                </div>
              </div>
            )}

            {/* Divider before Database Tables */}
            {!isSidebarCollapsed && (
              <div className="border-t border-gray-200 mb-1 flex-shrink-0" />
            )}

            {/* Database Tables Section - Always show (scrolls with rest of nav) */}
            {!isSidebarCollapsed && (
              <div className="flex-shrink-0 px-3 py-2">
                <DatabaseTablesSidebar 
                  tables={DATABASE_TABLES}
                  onTableSelect={(table) => {
                    setSelectedTable(table);
                    navigate(`/o/${orgIdFromPath}/control-room/${table.name}`);
                  }}
                  selectedTable={selectedTable}
                />
              </div>
            )}

            {/* Logout Button - Only visible when expanded */}
            {!isSidebarCollapsed && (
              <div className="pt-2 sm:pt-3 border-t border-gray-200 flex-shrink-0">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center space-x-2 sm:space-x-3 px-2 sm:px-3 py-1.5 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors min-h-44"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span className="text-xs sm:text-sm font-normal">Logout</span>
                </button>
              </div>
            )}
            </div>
          </div>

        </div>
      )}

      {/* Main Content - Fixed Header, Scrollable Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar with menu toggle */}
        {isMobile && (
          <div className="sticky top-0 z-40 bg-white border-b border-gray-200 px-3 py-2 flex items-center">
            <button
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Toggle menu"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="ml-2 font-medium text-gray-900">Control Room</div>
          </div>
        )}
        {/* Main Content - Scrollable */}
        <main className="flex-1 overflow-y-auto">
              {location.pathname.includes('client-integrations') ? (
                <ClientIntegrationsPage orgId={orgIdFromPath || ''} />
              ) : location.pathname.includes('parent-social-media') ? (
                <ParentSocialMediaSettingsPage />
              ) : location.pathname.includes('/control-room/plans') ||
                location.pathname.includes('/control-room/pricing-rules') ||
                location.pathname.includes('/control-room/invoices') ||
                location.pathname.includes('/control-room/runs') ||
                location.pathname.includes('/control-room/billing-settings') ? (
                <BillingPage orgId={orgIdFromPath || ''} />
              ) : selectedTable ? (
                <div className="flex-1 flex flex-col">
                  {/* Page Header with Search - Fixed */}
                  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white sticky top-0 z-40">
                    <div className="flex items-center space-x-4">
                      <div className="text-2xl">{selectedTable.icon}</div>
                      <div>
                        <h1 className="text-2xl font-bold text-gray-900">{selectedTable.displayName}</h1>
                        <p className="text-gray-600">{selectedTable.description}</p>
                      </div>
                    </div>
                        <div className="flex items-center space-x-3 flex-1 max-w-md ml-8">
                          <input
                            type="text"
                            placeholder={`Search ${selectedTable.displayName.toLowerCase()}...`}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                          {selectedTable?.name === 'organisations' ? (
                            <button
                              onClick={() => setIsAddOrganizationModalOpen(true)}
                              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                              title="Add new organization"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              <span className="text-sm font-medium">Data</span>
                            </button>
                          ) : selectedTable?.name === 'users' ? (
                            <button
                              onClick={() => setIsAddUserModalOpen(true)}
                              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                              title="Add new user"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              <span className="text-sm font-medium">Data</span>
                            </button>
                          ) : selectedTable?.name === 'user_organisations' ? (
                            <button
                              onClick={() => setIsAddUserOrgModalOpen(true)}
                              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                              title="Add new user organisation"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              <span className="text-sm font-medium">Data</span>
                            </button>
                          ) : selectedTable?.name === 'api_keys' ? (
                            <button
                              onClick={() => setIsAddApiKeyModalOpen(true)}
                              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                              title="Add new API key"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              <span className="text-sm font-medium">Data</span>
                            </button>
                          ) : selectedTable?.name === 'app_access' ? (
                            <button
                              onClick={() => setIsAddAppAccessModalOpen(true)}
                              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                              title="Add new app access"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              <span className="text-sm font-medium">Data</span>
                            </button>
                          ) : selectedTable?.name === 'tournaments' ? (
                            <button
                              onClick={() => setIsAddTournamentModalOpen(true)}
                              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                              title="Add new tournament"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              <span className="text-sm font-medium">Data</span>
                            </button>
                          ) : selectedTable?.name === 'seasons' ? (
                            <button
                              onClick={() => setIsAddSeasonModalOpen(true)}
                              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                              title="Add new season"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              <span className="text-sm font-medium">Data</span>
                            </button>
                          ) : selectedTable?.name === 'teams' ? (
                            <button
                              onClick={() => setIsAddTeamModalOpen(true)}
                              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                              title="Add new team"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              <span className="text-sm font-medium">Data</span>
                            </button>
                          ) : selectedTable?.name === 'players' ? (
                            <button
                              onClick={() => setIsAddPlayerModalOpen(true)}
                              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                              title="Add new player"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              <span className="text-sm font-medium">Data</span>
                            </button>
                          ) : selectedTable?.name === 'tournament_access' ? (
                            <button
                              onClick={() => setIsAddTournamentAccessModalOpen(true)}
                              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                              title="Add new tournament access"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              <span className="text-sm font-medium">Data</span>
                            </button>
                          ) : selectedTable?.name === 'team_access' ? (
                            <button
                              onClick={() => setIsAddTeamAccessModalOpen(true)}
                              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                              title="Add new team access"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              <span className="text-sm font-medium">Data</span>
                            </button>
                          ) : selectedTable?.name === 'season_access' ? (
                            <button
                              onClick={() => setIsAddSeasonAccessModalOpen(true)}
                              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                              title="Add new season access"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              <span className="text-sm font-medium">Data</span>
                            </button>
                          ) : selectedTable?.name === 'player_access' ? (
                            <button
                              onClick={() => setIsAddPlayerAccessModalOpen(true)}
                              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                              title="Add new player access"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              <span className="text-sm font-medium">Data</span>
                            </button>
                          ) : selectedTable?.name === 'stat_types' ? (
                            <button
                              onClick={() => setIsAddStatTypeModalOpen(true)}
                              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                              title="Add new stat type"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              <span className="text-sm font-medium">Data</span>
                            </button>
                          ) : (
                            <button
                              onClick={() => setIsAddModalOpen(true)}
                              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                              title="Add new data"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              <span className="text-sm font-medium">Data</span>
                            </button>
                          )}
                        </div>
                  </div>
                  
                  {/* Table Content */}
                  <div className="flex-1 p-6 overflow-y-auto">
                        <TableView
                          table={selectedTable}
                          searchQuery={searchQuery}
                          refreshTrigger={refreshCounter}
                          onEdit={handleEditEntity}
                        />
                  </div>
                </div>
              ) : (
            <div className="flex-1 p-6 overflow-y-auto">
              <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                <div className="mb-4">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">🎮</span>
                  </div>
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">Control Room</h1>
                  <p className="text-gray-600">
                    Select a database table from the sidebar to view and manage its data.
                  </p>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
      
          {/* Add Data Modal */}
          <AddDataModal
            isOpen={isAddModalOpen}
            onClose={() => {
              setIsAddModalOpen(false);
              setEditingEntity(null);
            }}
            onSave={handleSaveEntity}
            organization={editingEntity}
          />
          
          {/* Edit User Organisation Modal */}
          <EditUserOrganisationModal
            isOpen={isEditUserOrgModalOpen}
            onClose={() => {
              setIsEditUserOrgModalOpen(false);
              setEditingUserOrg(null);
            }}
            onSave={handleSaveUserOrg}
            userOrganisation={editingUserOrg}
          />

          {/* Add User Modal */}
          <AddUserModal
            isOpen={isAddUserModalOpen}
            onClose={() => setIsAddUserModalOpen(false)}
            onSaved={() => setRefreshCounter(prev => prev + 1)}
          />

          {/* Edit User Modal */}
          <EditUserModal
            isOpen={isEditUserModalOpen}
            onClose={() => {
              setIsEditUserModalOpen(false);
              setEditingUser(null);
            }}
            onSaved={() => setRefreshCounter(prev => prev + 1)}
            user={editingUser}
          />

          {/* Add Organization Modal */}
          <AddOrganizationModal
            isOpen={isAddOrganizationModalOpen}
            onClose={() => setIsAddOrganizationModalOpen(false)}
            onSaved={() => setRefreshCounter(prev => prev + 1)}
          />

          {/* Edit Organization Modal */}
          <EditOrganizationModal
            isOpen={isEditOrganizationModalOpen}
            onClose={() => {
              setIsEditOrganizationModalOpen(false);
              setEditingOrganization(null);
            }}
            onSaved={() => setRefreshCounter(prev => prev + 1)}
            organization={editingOrganization}
          />
          
          {/* Add User Organisation Modal */}
          <AddUserOrganisationModal
            isOpen={isAddUserOrgModalOpen}
            onClose={() => setIsAddUserOrgModalOpen(false)}
            onSave={handleAddUserOrg}
          />
          
          {/* Edit API Key Modal */}
          <EditApiKeyModal
            isOpen={isEditApiKeyModalOpen}
            onClose={() => {
              setIsEditApiKeyModalOpen(false);
              setEditingApiKey(null);
            }}
            onSave={handleSaveApiKey}
            apiKey={editingApiKey}
          />
          
          {/* Add API Key Modal */}
          <AddApiKeyModal
            isOpen={isAddApiKeyModalOpen}
            onClose={() => setIsAddApiKeyModalOpen(false)}
            onSave={handleAddApiKey}
          />
          
          {/* Edit Tournament Access Modal */}
          <EditTournamentAccessModal
            isOpen={isEditTournamentAccessModalOpen}
            onClose={() => {
              setIsEditTournamentAccessModalOpen(false);
              setEditingTournamentAccess(null);
            }}
            onSave={handleSaveTournamentAccess}
            tournamentAccess={editingTournamentAccess}
          />
          
          {/* Add Tournament Access Modal */}
          <AddTournamentAccessModal
            isOpen={isAddTournamentAccessModalOpen}
            onClose={() => setIsAddTournamentAccessModalOpen(false)}
            onSave={handleAddTournamentAccess}
          />

          {/* Edit App Access Modal */}
          <EditAppAccessModal
            isOpen={isEditAppAccessModalOpen}
            onClose={() => {
              setIsEditAppAccessModalOpen(false);
              setEditingAppAccess(null);
            }}
            onSave={handleSaveAppAccess}
            appAccess={editingAppAccess}
          />
          
          {/* Add App Access Modal */}
          <AddAppAccessModal
            isOpen={isAddAppAccessModalOpen}
            onClose={() => setIsAddAppAccessModalOpen(false)}
            onSave={handleAddAppAccess}
          />

          {/* Edit Tournament Modal */}
          <EditTournamentModal
            isOpen={isEditTournamentModalOpen}
            onClose={() => setIsEditTournamentModalOpen(false)}
            onSave={handleSaveTournament}
            tournament={editingTournament}
          />

          {/* Add Tournament Modal */}
          <AddTournamentModal
            isOpen={isAddTournamentModalOpen}
            onClose={() => setIsAddTournamentModalOpen(false)}
            onSave={handleAddTournament}
          />

          {/* Edit Season Modal */}
          <EditSeasonModal
            isOpen={isEditSeasonModalOpen}
            onClose={() => setIsEditSeasonModalOpen(false)}
            onSave={handleSaveSeason}
            season={editingSeason}
          />

          {/* Add Season Modal */}
          <AddSeasonModal
            isOpen={isAddSeasonModalOpen}
            onClose={() => setIsAddSeasonModalOpen(false)}
            onSave={handleAddSeason}
          />

          {/* Edit Team Modal */}
          <EditTeamModal
            isOpen={isEditTeamModalOpen}
            onClose={() => setIsEditTeamModalOpen(false)}
            onSave={handleSaveTeam}
            team={editingTeam}
          />

          {/* Add Team Modal */}
          <AddTeamModal
            isOpen={isAddTeamModalOpen}
            onClose={() => setIsAddTeamModalOpen(false)}
            onSave={handleAddTeam}
          />

          {/* Edit Player Modal */}
          <EditPlayerModal
            isOpen={isEditPlayerModalOpen}
            onClose={() => setIsEditPlayerModalOpen(false)}
            onSave={handleSavePlayer}
            player={editingPlayer}
          />

          {/* Add Player Modal */}
          <AddPlayerModal
            isOpen={isAddPlayerModalOpen}
            onClose={() => setIsAddPlayerModalOpen(false)}
            onSave={handleAddPlayer}
          />

          {/* Add Team Access Modal */}
          <AddTeamAccessModal
            isOpen={isAddTeamAccessModalOpen}
            onClose={() => setIsAddTeamAccessModalOpen(false)}
            onSave={handleAddTeamAccess}
          />

          {/* Edit Team Access Modal */}
          <EditTeamAccessModal
            isOpen={isEditTeamAccessModalOpen}
            onClose={() => { setIsEditTeamAccessModalOpen(false); setEditingTeamAccess(null); }}
            onSave={async (data) => {
              if (!editingTeamAccess) return;
              await teamAccessService.update(editingTeamAccess.id, data);
              setRefreshCounter(prev => prev + 1);
            }}
            teamAccess={editingTeamAccess}
          />

          {/* Add Player Access Modal */}
          <AddPlayerAccessModal
            isOpen={isAddPlayerAccessModalOpen}
            onClose={() => setIsAddPlayerAccessModalOpen(false)}
            onSave={handleAddPlayerAccess}
          />

          {/* Edit Player Access Modal */}
          <EditPlayerAccessModal
            isOpen={isEditPlayerAccessModalOpen}
            onClose={() => { setIsEditPlayerAccessModalOpen(false); setEditingPlayerAccess(null); }}
            onSave={async (data) => {
              if (!editingPlayerAccess) return;
              await playerAccessService.update(editingPlayerAccess.id, data);
              setRefreshCounter(prev => prev + 1);
            }}
            playerAccess={editingPlayerAccess}
          />

          {/* Add Season Access Modal */}
          <AddSeasonAccessModal
            isOpen={isAddSeasonAccessModalOpen}
            onClose={() => setIsAddSeasonAccessModalOpen(false)}
            onSave={handleAddSeasonAccess}
          />

          {/* Edit Season Access Modal */}
          <EditSeasonAccessModal
            isOpen={isEditSeasonAccessModalOpen}
            onClose={() => { setIsEditSeasonAccessModalOpen(false); setEditingSeasonAccess(null); }}
            onSave={async (data) => {
              if (!editingSeasonAccess) return;
              await seasonAccessService.update(editingSeasonAccess.id, data);
              setRefreshCounter(prev => prev + 1);
            }}
            seasonAccess={editingSeasonAccess}
          />

          {/* Edit Stat Type Modal */}
          <EditStatTypeModal
            isOpen={isEditStatTypeModalOpen}
            onClose={() => { setIsEditStatTypeModalOpen(false); setEditingStatType(null); }}
            onSave={handleSaveStatType}
            statType={editingStatType}
          />

          {/* Add Stat Type Modal */}
          <AddStatTypeModal
            isOpen={isAddStatTypeModalOpen}
            onClose={() => setIsAddStatTypeModalOpen(false)}
            onSave={handleAddStatType}
          />

          {/* Toast Notification */}
          <Toast
            isVisible={toast.isVisible}
            onClose={() => setToast(prev => ({ ...prev, isVisible: false }))}
            type={toast.type}
            message={toast.message}
            duration={6000}
          />
        </div>
  );
};

export default ControlRoomApp;
